/**
 * Client-side filter matching utility for FilterBarV2
 * Matches the backend logic in backend/utils/filterMatcher.js
 */

import { FilterState, FilterGroup, FilterCondition } from '../types/filters';

// Mirror of the backend's buildContainsPattern (utils/searchTerms.js):
// whole-word, case-insensitive, optional plural "s" — so "AI" doesn't match
// "daily" and client-side counts agree with the server's filter SQL.
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const wholeWordMatches = (text: string, term: string): boolean => {
  const t = String(term ?? '').trim();
  if (!t) return false;
  const isWord = (ch: string) => /[A-Za-z0-9_]/.test(ch);
  const prefix = isWord(t[0]) ? '\\b' : '';
  const suffix = isWord(t[t.length - 1]) ? '(?:s)?\\b' : '';
  try {
    return new RegExp(`${prefix}${escapeRegex(t)}${suffix}`, 'i').test(text);
  } catch {
    return text.toLowerCase().includes(t.toLowerCase());
  }
};

/**
 * Check if a value matches a filter condition
 */
const matchesCondition = (itemValue: any, conditionValue: string, operator: string, conditionValues?: string[]): boolean => {
  // Boolean flag columns: "Yes" = strictly true; "No / never" = false or unset.
  // Handled before the null-guard so "never" matches an unset flag.
  if (operator === 'is_true' || operator === 'is_false') {
    const truthy = itemValue === true || itemValue === 'true' || itemValue === 't' || itemValue === 1;
    return operator === 'is_true' ? truthy : !truthy;
  }

  // Handle null/undefined
  if (itemValue === null || itemValue === undefined) {
    return operator === 'is_empty';
  }

  if (operator === 'is_not_empty') {
    if (Array.isArray(itemValue)) return itemValue.length > 0;
    return itemValue !== null && itemValue !== undefined && itemValue !== '';
  }

  if (operator === 'is_empty') {
    if (Array.isArray(itemValue)) return itemValue.length === 0;
    return itemValue === null || itemValue === undefined || itemValue === '';
  }

  // contains / not_contains: match the backend exactly — multi-token chips
  // (values[], e.g. "risk + assessment") require ALL tokens to appear in the
  // field, each as a whole word. Single-value chips use the same whole-word
  // rule. This is what the server's buildFilterSQL does, so table counts and
  // analytics counts stay in agreement.
  if (operator === 'contains' || operator === 'not_contains') {
    const text = Array.isArray(itemValue) ? itemValue.join(' ') : String(itemValue);
    const terms = (conditionValues && conditionValues.length > 0)
      ? conditionValues
      : [conditionValue];
    const allMatch = terms.every(t => wholeWordMatches(text, t));
    return operator === 'contains' ? allMatch : !allMatch;
  }

  // Convert to strings for comparison
  const itemStr = String(itemValue).toLowerCase();
  const condStr = String(conditionValue).toLowerCase();

  // Handle array values (like skills, tags)
  if (Array.isArray(itemValue)) {
    const arrayStr = itemValue.join(' ').toLowerCase();
    switch (operator) {
      case 'contains':
        return arrayStr.includes(condStr);
      case 'not_contains':
        return !arrayStr.includes(condStr);
      case 'equals':
        return itemValue.some(v => String(v).toLowerCase() === condStr);
      case 'not_equals':
        return !itemValue.some(v => String(v).toLowerCase() === condStr);
      default:
        return false;
    }
  }

  // Numeric comparisons
  if (operator === 'greater_than' || operator === 'less_than' ||
      operator === 'greater_equal' || operator === 'less_equal') {
    const numItem = parseFloat(itemValue);
    const numCond = parseFloat(conditionValue);

    if (isNaN(numItem) || isNaN(numCond)) return false;

    switch (operator) {
      case 'greater_than': return numItem > numCond;
      case 'less_than': return numItem < numCond;
      case 'greater_equal': return numItem >= numCond;
      case 'less_equal': return numItem <= numCond;
    }
  }

  // String comparisons
  switch (operator) {
    case 'contains':
      return itemStr.includes(condStr);
    case 'not_contains':
      return !itemStr.includes(condStr);
    case 'equals':
      return itemStr === condStr;
    case 'not_equals':
      return itemStr !== condStr;
    case 'starts_with':
      return itemStr.startsWith(condStr);
    case 'ends_with':
      return itemStr.endsWith(condStr);
    default:
      return false;
  }
};

/**
 * Check if an item matches a single filter condition
 */
const itemMatchesCondition = (item: any, condition: FilterCondition): boolean => {
  // Check if this is a custom column (exists in custom_data)
  let itemValue;

  if (item.custom_data && item.custom_data[condition.column] !== undefined) {
    // Custom column from JSONB
    itemValue = item.custom_data[condition.column];
  } else {
    // Standard column
    itemValue = item[condition.column];
  }

  return matchesCondition(itemValue, condition.value, condition.operator, condition.values);
};

/**
 * Check if an item matches a filter group (with OR/AND logic for conditions)
 */
const itemMatchesGroup = (item: any, group: FilterGroup): boolean => {
  if (!group.conditions || group.conditions.length === 0) {
    return true;
  }

  if (group.logic === 'OR') {
    // OR logic: item matches if ANY condition matches
    return group.conditions.some(condition => itemMatchesCondition(item, condition));
  } else {
    // AND logic: item matches if ALL conditions match
    return group.conditions.every(condition => itemMatchesCondition(item, condition));
  }
};

/**
 * Check if an item matches the entire filter state (all groups must match - AND between groups)
 */
export const itemMatchesFilterState = (item: any, filterState: FilterState): boolean => {
  // All groups must match (AND logic between groups)
  if (filterState.groups && filterState.groups.length > 0) {
    const groupsMatch = filterState.groups.every(group => itemMatchesGroup(item, group));
    if (!groupsMatch) return false;
  }

  // Check employment types (if any)
  if (filterState.employmentTypes && filterState.employmentTypes.length > 0) {
    if (!item.current_contract_type && !item.employment_type) return false;
    const employmentField = item.current_contract_type || item.employment_type;
    const employmentMatch = filterState.employmentTypes.some(type =>
      employmentField.toLowerCase().includes(type.toLowerCase())
    );
    if (!employmentMatch) return false;
  }

  return true;
};

/**
 * Filter an array of items by filter state
 */
export const filterItems = <T extends any>(items: T[], filterState: FilterState): T[] => {
  if (!filterState ||
      (!filterState.groups?.length &&
       !filterState.employmentTypes?.length)) {
    return items;
  }

  return items.filter(item => itemMatchesFilterState(item, filterState));
};

/**
 * Filter companies by entityType
 */
export const filterCompaniesByState = (companies: any[], filterState: FilterState): any[] => {
  const companyFilterState: FilterState = {
    groups: filterState.groups.filter(g =>
      g.conditions.every(c => c.entityType === 'company')
    ),
    employmentTypes: []
  };

  return filterItems(companies, companyFilterState);
};

/**
 * Filter candidates by entityType
 */
export const filterCandidatesByState = (candidates: any[], filterState: FilterState): any[] => {
  const candidateFilterState: FilterState = {
    groups: filterState.groups.filter(g =>
      g.conditions.every(c => c.entityType === 'candidate')
    ),
    employmentTypes: filterState.employmentTypes || []
  };

  return filterItems(candidates, candidateFilterState);
};

/**
 * Filter managers by entityType
 */
export const filterManagersByState = (managers: any[], filterState: FilterState): any[] => {
  const managerFilterState: FilterState = {
    groups: filterState.groups.filter(g =>
      g.conditions.every(c => c.entityType === 'manager')
    ),
    employmentTypes: []
  };

  return filterItems(managers, managerFilterState);
};
